import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useMutation, useQuery, gql } from '@apollo/client'
import './PaymentPage.css'

const GET_ORDER = gql`
  query GetOrder($orderId: String!) {
    getOrder(orderId: $orderId) {
      id
      orderId
      userId
      amount
      phone
      operatorId
      planId
      status
      createdAt
    }
  }
`

const INITIATE_PAYMENT = gql`
  mutation InitiatePayment($orderId: String!) {
    initiatePayment(orderId: $orderId) {
      status
      message
      hash
      transactionId
      paymentLink
    }
  }
`

function PaymentPage() {
  const { orderId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [showPaymentForm, setShowPaymentForm] = useState(false)

  const { data: orderData, loading: orderLoading } = useQuery(GET_ORDER, {
    variables: { orderId },
    onError: () => navigate('/', { state: { from: location } })
  })

  const [initiatePayment, { loading: paymentLoading }] = useMutation(INITIATE_PAYMENT, {
    onCompleted: (data) => {
      if (data.initiatePayment.status === 'success') {
        // Create a form to submit to PayU
        createPayUForm(data.initiatePayment)
      }
    },
    onError: (error) => {
      alert(`Payment initiation failed: ${error.message}`)
    }
  })

  const createPayUForm = (paymentData) => {
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = 'https://secure.payu.in/_payment'

    const fields = {
      key: process.env.REACT_APP_PAYU_MERCHANT_KEY || '',
      txnid: paymentData.transactionId,
      amount: orderData.getOrder.amount,
      productinfo: `Recharge for ${orderData.getOrder.phone}`,
      firstname: 'Customer',
      email: 'customer@morrecharz.com',
      phone: orderData.getOrder.phone,
      surl: `${window.location.origin}/payment-success`,
      furl: `${window.location.origin}/payment-failed`,
      hash: paymentData.hash
    }

    Object.keys(fields).forEach(key => {
      if (fields[key]) {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = key
        input.value = fields[key]
        form.appendChild(input)
      }
    })

    document.body.appendChild(form)
    form.submit()
  }

  const handleInitiatePayment = () => {
    if (orderData?.getOrder) {
      initiatePayment({ variables: { orderId } })
    }
  }

  if (orderLoading) {
    return (
      <div className="payment-page">
        <div className="container">
          <div className="loading"><div className="spinner"></div></div>
        </div>
      </div>
    )
  }

  const order = orderData?.getOrder
  if (!order) {
    return (
      <div className="payment-page">
        <div className="container">
          <div className="error-container">
            <h3>Order Not Found</h3>
            <button className="btn-primary" onClick={() => navigate('/', { state: { from: location } })}>
              Go Back Home
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="payment-page">
      <div className="container">
        <div className="payment-container">
          <div className="order-summary">
            <h2>Order Summary</h2>
            <div className="summary-info">
              <div className="info-row">
                <span className="label">Order ID:</span>
                <span className="value">{order.orderId}</span>
              </div>
              <div className="info-row">
                <span className="label">Phone Number:</span>
                <span className="value">{order.phone}</span>
              </div>
              <div className="info-row">
                <span className="label">Amount:</span>
                <span className="value amount">₹{order.amount}</span>
              </div>
              <div className="info-row">
                <span className="label">Status:</span>
                <span className={`value status ${order.status}`}>{order.status.toUpperCase()}</span>
              </div>
            </div>
          </div>

          <div className="payment-method">
            <h2>Payment Method</h2>
            <div className="payu-option">
              <div className="option-header">
                <input type="radio" id="payu" name="payment" defaultChecked />
                <label htmlFor="payu">PayU Payment Gateway</label>
              </div>
              <p className="option-desc">
                Secure online payment via PayU. Supports all major cards, UPI, and digital wallets.
              </p>
            </div>

            <div className="payment-details">
              <h3>Payment Details</h3>
              <div className="detail">
                <span>Recharge Amount:</span>
                <strong>₹{order.amount}</strong>
              </div>
              <div className="detail">
                <span>Processing Fee:</span>
                <strong>₹0</strong>
              </div>
              <div className="detail total">
                <span>Total Amount:</span>
                <strong>₹{order.amount}</strong>
              </div>
            </div>

            <button
              className="btn-primary proceed-payment-btn"
              onClick={handleInitiatePayment}
              disabled={paymentLoading}
            >
              {paymentLoading ? 'Processing...' : 'Proceed to PayU'}
            </button>

            <div className="security-info">
              <p>🔒 Your payment is secured with SSL encryption. All transactions are safe and secure.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PaymentPage
